import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Box, Autocomplete } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import Loading from "@components/Loading";
import { Unit } from "@lib/types/units";
import { getUnits } from "@lib/api/queries/units";
import { BaseUnitGroupResponse, UnitGroup } from "@lib/types/unit_group";
import {
  UnitGroupFormData,
  unitGroupSchema,
  updateUnitGroup,
} from "@lib/api/mutations/unit_groups";

interface Props {
  updateUnitGroupDialogOpen: boolean;
  setUpdateUnitGroupDialogOpen: (open: boolean) => void;
  unitGroupData?: UnitGroup;
  refetch?: () => void;
}

const UpdateUnitGroup: React.FC<Props> = ({
  updateUnitGroupDialogOpen,
  setUpdateUnitGroupDialogOpen,
  unitGroupData,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UnitGroupFormData>({
    resolver: zodResolver(unitGroupSchema),
    defaultValues: {
      name: unitGroupData?.name || "",
      units: unitGroupData?.units.map((unitGroup) => unitGroup.unit_id) || [],
    },
  });

  useEffect(() => {
    if (unitGroupData) {
      setValue("name", unitGroupData.name);
      setValue(
        "units",
        unitGroupData.units.map((unit) => unit.unit_id)
      );
    }
  }, [unitGroupData, updateUnitGroupDialogOpen]);

  const { mutate, isPending: isUpdatingUnitGroup } = useMutation({
    mutationFn: updateUnitGroup,
    onSuccess: (data: BaseUnitGroupResponse) => {
      showSnackbar(t("user.unitGroups.updateSuccess"), "success");
      setUpdateUnitGroupDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const {
    data: units,
    isFetching,
    refetch: refetchUnits,
    error,
  } = useQuery<Unit[]>({
    queryKey: ["units"],
    queryFn: async () => {
      const units = await getUnits();
      return units.units;
    },
    enabled: updateUnitGroupDialogOpen,
  });

  const handleUpdateUnitGroup = (data: UnitGroupFormData) => {
    if (!unitGroupData?.group_id) return;

    mutate({
      unitGroupId: unitGroupData.group_id,
      updateUnitGroupParams: data,
    });
  };

  return (
    <FormDialog
      title={t("user.unitGroups.update")}
      open={updateUnitGroupDialogOpen}
      onClose={() => {
        setUpdateUnitGroupDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleUpdateUnitGroup)}
      submitText={t("general.save")}
      isSubmitting={isUpdatingUnitGroup}
      submittingText={t("general.saving")}
    >
      {isFetching && <Loading />}

      {!isFetching && units && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("general.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  inputProps={{
                    readOnly: true,
                  }}
                />
              )}
            />

            <Controller
              name="units"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field}
                    multiple
                    options={units || []}
                    value={
                      units?.filter((unit) =>
                        field.value?.includes(unit.unit_id)
                      ) || []
                    }
                    onChange={(_, value) => {
                      field.onChange(
                        value ? value.map((unit) => unit.unit_id) : []
                      );
                    }}
                    loading={false}
                    isOptionEqualToValue={(option, value) =>
                      option.unit_id === value.unit_id
                    }
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.name}-${option.unit_id}`}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.unitGroups.units")}
                        placeholder={t("user.unitGroups.selectUnits")}
                        variant="outlined"
                        error={!!errors.units}
                        helperText={errors.units?.message}
                      />
                    )}
                  />
                </div>
              )}
            />
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default UpdateUnitGroup;
